import React from "react";
import { useAuth } from '../context/AuthContext';
import { Role } from '../types';

const Dashboard: React.FC = () => {
  const { user, isAdmin, isManager, isEmployee } = useAuth();

  const getRoleColor = (role: string | Role): string => {
    const roleName = typeof role === 'string' ? role : role.name;
    const colors: { [key: string]: string } = {
      admin: '#dc2626',
      manager: '#2563eb',
      employee: '#059669'
    };
    return colors[roleName] || '#6b7280';
  };

  const getWelcomeMessage = (): string => {
    if (isAdmin()) return '¡Bienvenido, Administrador!';
    if (isManager()) return '¡Bienvenido, Manager!';
    if (isEmployee()) return '¡Bienvenido, Empleado!';
    return '¡Bienvenido!';
  };

  const getPermissions = (): string[] => {
    const permissions: string[] = [];

    if (isAdmin()) {
      permissions.push(
        'Gestión completa de productos',
        'Administración de usuarios',
        'Acceso a todos los reportes',
        'Configuración del sistema'
      );
    } else if (isManager()) {
      permissions.push(
        'Crear y editar productos',
        'Ver reportes de ventas',
        'Gestionar inventario'
      );
    } else if (isEmployee()) {
      permissions.push(
        'Ver productos',
        'Actualizar información básica',
        'Consultar inventario'
      );
    }

    return permissions;
  };

  return (
    <div className="dashboard">
      <div className="welcome-section">
        <h1>{getWelcomeMessage()}</h1>
        <p>Estás conectado como <strong>{user?.email}</strong></p>
      </div>

      <div className="cards-grid">
        {/* Información del usuario */}
        <div className="card">
          <h2>Tu Perfil</h2>
          <div className="user-info">
            <p><strong>Email:</strong> {user?.email}</p>
            <p><strong>Nombre:</strong> {user?.firstName} {user?.lastName}</p>
            <div className="roles-container">
              <strong>Roles:</strong>
              <div className="roles-list">
                {Array.isArray(user?.roles) && user.roles.map((role: Role | any) => {
                  const roleName = typeof role === 'string' ? role : role.name;
                  return (
                    <span
                      key={roleName}
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(role) }}
                    >
                      {roleName.toUpperCase()}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Permisos */}
        <div className="card">
          <h2>Tus Permisos</h2>
          <ul className="permissions-list">
            {getPermissions().map((permission, index) => (
              <li key={index}>✅ {permission}</li>
            ))}
          </ul>
        </div>

        {/* Acciones rápidas */}
        <div className="card">
          <h2>Acciones Rápidas</h2>
          <div className="quick-actions">
            {(isManager() || isAdmin()) && (
              <button className="action-button">
                📦 Gestionar Productos
              </button>
            )}

            {isAdmin() && (
              <button className="action-button">
                👥 Administrar Usuarios
              </button>
            )}

            <button className="action-button">
              📊 Ver Reportes
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .dashboard {
          max-width: 1200px;
          margin: 0 auto;
        }

        .welcome-section {
          margin-bottom: 32px;
          text-align: center;
        }

        .welcome-section h1 {
          color: #1f2937;
          margin-bottom: 8px;
          font-size: 2.5rem;
        }

        .welcome-section p {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .card h2 {
          color: #1f2937;
          margin-bottom: 16px;
          font-size: 1.25rem;
        }

        .user-info p {
          margin-bottom: 8px;
          color: #374151;
        }

        .roles-container {
          margin-top: 12px;
        }

        .roles-list {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        .role-badge {
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .permissions-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .permissions-list li {
          padding: 8px 0;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
        }

        .permissions-list li:last-child {
          border-bottom: none;
        }

        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .action-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .action-button:hover {
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .welcome-section h1 {
            font-size: 2rem;
          }
          
          .cards-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;